import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "./ui/dialog";
import { Network, Trash2, ArrowRight } from "lucide-react";
import { toast } from "sonner";

// Per-provider field set. The backend's create_insertion fns each declare a
// strict shape (see crates/rustwall-cloud/src/{aws,azure,gcp,oracle}.rs); this
// mirrors them so the operator only sees the fields their cloud actually
// needs. CIDR list defaults to 0.0.0.0/0 — Phase 3 ships full-egress
// inspection by default and lets operators narrow it from the wizard.
const SCHEMA = {
  aws: [
    { k: "vpc_id",            label: "VPC ID",              hint: "vpc-…" },
    { k: "subnet_ids",        label: "Subnets (one per AZ)", list: true,  hint: "subnet-… subnet-…" },
    { k: "appliance_eni_id",  label: "Appliance ENI",       hint: "eni-…" },
    { k: "route_table_ids",   label: "Customer route tables", list: true, hint: "rtb-… rtb-…" },
    { k: "cidrs",             label: "Redirect CIDRs",      list: true, default: "0.0.0.0/0" },
  ],
  azure: [
    { k: "resource_group",       label: "Resource group" },
    { k: "location",             label: "Location",       hint: "eastus" },
    { k: "appliance_subnet_id",  label: "Appliance subnet ID", hint: "/subscriptions/.../subnets/…" },
    { k: "appliance_nic_ids",    label: "Appliance NIC IDs",   list: true },
    { k: "customer_subnet_ids",  label: "Customer subnet IDs", list: true },
    { k: "cidrs",                label: "Redirect CIDRs",      list: true, default: "0.0.0.0/0" },
  ],
  gcp: [
    { k: "region",                 label: "Region",       hint: "us-central1" },
    { k: "network",                label: "Network",      hint: "projects/<p>/global/networks/<vpc>" },
    { k: "subnetwork",             label: "Subnetwork",   hint: "projects/<p>/regions/<r>/subnetworks/<sn>" },
    { k: "appliance_instances",    label: "Appliance VM self-links", list: true,
      hint: "projects/<p>/zones/<zone>/instances/<vm>" },
    { k: "cidrs",                  label: "Redirect CIDRs", list: true, default: "0.0.0.0/0" },
  ],
  oracle: [
    { k: "compartment_id",          label: "Compartment OCID" },
    { k: "subnet_id",               label: "Appliance subnet OCID" },
    { k: "appliance_instance_ids",  label: "Appliance instance OCIDs", list: true },
    { k: "route_table_ids",         label: "Customer route table OCIDs", list: true },
    { k: "cidrs",                   label: "Redirect CIDRs", list: true, default: "0.0.0.0/0" },
  ],
  virtualexpanse: [
    { k: "virtual_network_id",      label: "Virtual Network ID", hint: "vnet-…" },
    { k: "appliance_endpoint_id",   label: "Appliance Endpoint ID", hint: "vep-…" },
    { k: "cidrs",                   label: "Redirect CIDRs", list: true, default: "0.0.0.0/0" },
  ],
  omnissa: [
    { k: "segment_id",              label: "NSX Segment ID", hint: "segment-…" },
    { k: "gateway_path",            label: "Tier-1 Gateway Path", hint: "/infra/tier-1s/…" },
    { k: "service_profile_id",      label: "Service Profile ID", hint: "sp-…" },
    { k: "cidrs",                   label: "Redirect CIDRs", list: true, default: "0.0.0.0/0" },
  ],
};

const PROVIDER_LABELS = {
  aws: "AWS · GWLB endpoint",
  azure: "Azure · Standard ILB + UDR",
  gcp: "GCP · ILB next-hop",
  oracle: "Oracle · NLB + route tables",
  virtualexpanse: "VirtualExpanse · VE Connector",
  omnissa: "Omnissa/OIC · NSX Service Insertion",
};

export default function InsertWizard({ appliance, onClose }) {
  const [provider, setProvider] = useState(appliance?.cloud || "aws");
  const [vals, setVals] = useState({});
  const [busy, setBusy] = useState(false);
  const [insertions, setInsertions] = useState([]);

  const loadInsertions = async () => {
    if (!appliance?.appliance_id) return;
    try {
      const { data } = await api.get(`/appliances/${appliance.appliance_id}/insert`);
      setInsertions(Array.isArray(data) ? data : []);
    } catch (e) { /* read-only — silent on failure */ }
  };
  useEffect(() => { loadInsertions(); }, [appliance?.appliance_id]); // eslint-disable-line react-hooks/exhaustive-deps

  // When provider changes, seed defaults (CIDR → 0.0.0.0/0).
  useEffect(() => {
    const seed = {};
    for (const f of SCHEMA[provider] || []) {
      if (f.default) seed[f.k] = f.default;
    }
    setVals(seed);
  }, [provider]);

  const update = (k, v) => setVals({ ...vals, [k]: v });

  // Normalize: list fields → arrays of trimmed non-empty strings.
  const buildParams = () => {
    const out = {};
    for (const f of SCHEMA[provider] || []) {
      const raw = vals[f.k];
      if (raw == null || raw === "") continue;
      if (f.list) {
        out[f.k] = String(raw).split(/[,\s]+/).map((s) => s.trim()).filter(Boolean);
      } else {
        out[f.k] = String(raw).trim();
      }
    }
    return out;
  };

  const submit = async () => {
    setBusy(true);
    try {
      const body = { provider, params: buildParams() };
      const { data } = await api.post(`/appliances/${appliance.appliance_id}/insert`, body);
      toast.success(`Inserted (${data.kind || "active"})`);
      await loadInsertions();
      setVals({});
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const tearDown = async (id) => {
    if (!confirm(`Tear down insertion ${id}? This will remove the load balancer/UDRs/routes RustWall created.`)) return;
    try {
      await api.delete(`/appliances/${appliance.appliance_id}/insert/${id}`);
      toast.success("Insertion deleted");
      await loadInsertions();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  return (
    <Dialog open={!!appliance} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-zinc-950 border-zinc-800 rounded-sm max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <Network className="w-4 h-4 text-cyan-400" />
            Insert {appliance?.appliance_id} into network
          </DialogTitle>
        </DialogHeader>

        <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">
          // PROVIDER
        </div>
        <div className="flex flex-wrap gap-2 mb-3">
          {Object.entries(PROVIDER_LABELS).map(([id, label]) => (
            <button
              key={id}
              type="button"
              onClick={() => setProvider(id)}
              className={`text-[10px] font-mono uppercase tracking-widest px-2 py-1 border rounded-sm
                ${provider === id ? "border-cyan-500 text-cyan-400 bg-cyan-500/10" : "border-zinc-800 text-zinc-400"}`}
              data-testid={`pick-provider-${id}`}
            >
              {label}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 gap-2 max-h-[40vh] overflow-y-auto pr-1">
          {(SCHEMA[provider] || []).map((f) => (
            <div key={f.k}>
              <Label className="text-[10px] font-mono uppercase tracking-widest text-zinc-400">
                {f.label}{f.list && <span className="text-zinc-600 normal-case ml-1 tracking-normal">(comma or whitespace separated)</span>}
              </Label>
              <Input
                value={vals[f.k] ?? ""}
                onChange={(e) => update(f.k, e.target.value)}
                placeholder={f.hint || ""}
                className="bg-zinc-900 border-zinc-800 rounded-sm font-mono text-xs"
                data-testid={`insert-field-${f.k}`}
              />
            </div>
          ))}
        </div>

        {insertions.length > 0 && (
          <div className="mt-3 border-t border-zinc-800 pt-3">
            <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-2">
              // ACTIVE INSERTIONS ({insertions.length})
            </div>
            <div className="space-y-1">
              {insertions.map((i) => (
                <div key={i.insertion_id} className="flex items-center justify-between border border-zinc-800 rounded-sm px-2 py-1.5">
                  <div className="text-xs font-mono">
                    <span className="text-cyan-400">{i.provider}</span>
                    <ArrowRight className="inline w-3 h-3 mx-1 text-zinc-600" />
                    <span className="text-zinc-300">{i.kind}</span>
                    <span className="text-zinc-500 ml-2">{i.insertion_id}</span>
                    <span className="text-zinc-600 ml-2">({(i.attachments || []).length} attachments)</span>
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => tearDown(i.insertion_id)} className="text-red-400 hover:text-red-300 gap-1.5">
                    <Trash2 className="w-3.5 h-3.5" /> Tear down
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button
            onClick={submit}
            disabled={busy}
            data-testid="insert-submit"
            className="bg-cyan-500 hover:bg-cyan-400 text-zinc-950 rounded-sm"
          >
            {busy ? "INSERTING…" : "INSERT"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
